import { takeLatest, call, put, select } from 'redux-saga/effects';
import { AxiosError, AxiosResponse } from 'axios';
import {
  inviteRecruiter,
  toggleRecruiterStatus,
} from '../../api/RecruiterDashboard/recruiter.api';
import { fetchRoles } from '../../api/RecruiterDashboard/roles.api';
import {
  INVITE_RECRUITER,
  TOGGLE_RECRUITER_STATUS,
} from '../../constants/ActionTypes';
import { AnyAction } from 'redux';
import { showAlert } from '../../actions/alert.action';
import { EAlertType } from '../../types/alert.types';
import { IInitialState } from '../../types/initialState.types';
import { fetchRolesSuccess } from '../../actions/RecruiterDashboard/roles.actions';
import { IRole } from '../../types/roles.types';

function* inviteRecruiterSaga(action: AnyAction): Generator<any, void, any> {
  try {
    const {
      userIdentityData: { username },
      company: { slug },
    } = yield select((state: IInitialState) => ({
      userIdentityData: state.auth.userIdentityData,
      company: state.company,
    }));

    if (!action.payload?.company_slug) {
      action.payload.company_slug = slug;
    }

    const response: AxiosResponse = yield call(
      inviteRecruiter,
      action.payload,
      username
    );

    if (response && response.data) {
      yield put(
        showAlert({
          show: true,
          message: 'Recruiter invited successfully',
          type: EAlertType.SUCCESS,
          version: 'v1',
        })
      );
    }
    const updatedRoles: IRole[] = yield call(fetchRoles, {
      username,
      companySlug: action.payload.company_slug,
    });
    yield put(fetchRolesSuccess(updatedRoles));
  } catch (error: unknown) {
    let errorMessage = 'Failed to invite recruiter';
    if (error && typeof error === 'object' && 'isAxiosError' in error) {
      const axiosError = error as AxiosError;

      if (
        axiosError.response?.data &&
        (axiosError.response.data as any).description
      ) {
        const serverMessage = axiosError.response.data as {
          description?: string;
        };
        errorMessage = serverMessage.description || errorMessage;
      } else if (axiosError.message) {
        errorMessage = axiosError.message;
      }
    }
    yield put(
      showAlert({
        show: true,
        message: errorMessage,
        type: EAlertType.ERROR,
        version: 'v1',
      })
    );
  }
}

function* toggleRecruiterStatusSaga(
  action: AnyAction
): Generator<any, void, any> {
  try {
    const {
      userIdentityData: { username },
    } = yield select((state: IInitialState) => ({
      userIdentityData: state.auth.userIdentityData,
    }));
    const {
      company_slug: companySlug,
      recruiter_id: id,
      makeActive,
    } = action.payload;
    const response: AxiosResponse = yield call(
      toggleRecruiterStatus,
      { company_slug: companySlug, recruiter_id: id },
      username
    );

    if (response && response.data) {
      yield put(
        showAlert({
          show: true,
          message: !makeActive
            ? 'Member made inactive.'
            : 'Member made active.',
          type: EAlertType.SUCCESS,
          version: 'v1',
        })
      );
    }

    const updatedRoles: IRole[] = yield call(fetchRoles, {
      username,
      companySlug,
    });
    yield put(fetchRolesSuccess(updatedRoles));
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to toggle recruiter status';
    yield put(
      showAlert({
        show: true,
        message: errorMessage,
        type: EAlertType.ERROR,
        version: 'v1',
      })
    );
  }
}

export default function* loadRecruiterSaga() {
  yield takeLatest(INVITE_RECRUITER, inviteRecruiterSaga);
  yield takeLatest(TOGGLE_RECRUITER_STATUS, toggleRecruiterStatusSaga);
}
